import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Chip
} from '@mui/material';
import fileSystemAccessServiceInstance from '../../services/FileSystemAccessService';
import HistoryIcon from '@mui/icons-material/History';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import AddCircleIcon from '@mui/icons-material/AddCircle';

const getActionIcon = (actionType) => {
  switch (actionType) {
    case 'CREATE_FOLDER':
      return <CreateNewFolderIcon color="success" />;
    case 'CREATE_VIDEO':
      return <VideoFileIcon color="success" />;
    case 'CREATE_CHILD':
      return <AddCircleIcon color="success" />;
    case 'CREATE_CHILD_FOLDER':
      return <AddCircleIcon color="success" />;
    case 'RENAME':
      return <DriveFileRenameOutlineIcon color="warning" />;
    case 'DELETE':
      return <DeleteIcon color="error" />;
    case 'MOVE':
      return <MoveToInboxIcon color="info" />;
    case 'RESTORE':
      return <HistoryIcon color="success" />;
    default:
      return <HistoryIcon color="action" />;
  }
};

const getActionDescription = (historyItem) => {
  switch (historyItem.actionType) {
    case 'CREATE_FOLDER':
      return 'Folder created';
    case 'CREATE_VIDEO':
      return `${historyItem.name} - Video added to folder`;
    case 'CREATE_CHILD':
      return 'Item added to folder';
    case 'CREATE_CHILD_FOLDER':
      return `${historyItem.name} - Subfolder created`; 
    case 'RENAME':
      return `Renamed from "${historyItem.oldName}" to "${historyItem.newName}"`;
    case 'DELETE':
      return `${historyItem.name} - deleted`;
    case 'MOVE':
      return `Moved from "${historyItem.oldPath}" to "${historyItem.newPath}"`;
    case 'RESTORE':
      return `${historyItem.name} - restored`;
    default:
      return 'Unknown action';
  }
};

const getActionColor = (actionType) => {
  switch (actionType) {
    case 'CREATE_FOLDER':
    case 'CREATE_VIDEO':
    case 'CREATE_CHILD':
      return 'success';
    case 'RENAME':
      return 'warning';
    case 'DELETE':
      return 'error';
    case 'MOVE':
      return 'info';
    default:
      return 'default';
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'Unknown';
  
  const date = new Date(dateString);
  return date.toLocaleString();
};

const FolderHistoryDialog = ({ open, onClose, folderId, folderName }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && folderId) {
      loadFolderHistory();
    }
  }, [open, folderId]);

  const loadFolderHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const historyData = await fileSystemAccessServiceInstance.getFolderHistory(folderId);
      setHistory(historyData);
    } catch (err) {
      console.error('Error loading folder history:', err);
      setError('Failed to load folder history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <HistoryIcon sx={{ mr: 1 }} />
        Folder History: {folderName}
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : history.length === 0 ? (
          <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
            No history records found for this folder
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Action</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Date & Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Chip 
                        label={item.actionType}
                        color={getActionColor(item.actionType)}
                        size="small"
                        icon={getActionIcon(item.actionType)}
                      />
                    </TableCell>
                    <TableCell>{getActionDescription(item)}</TableCell>
                    <TableCell>{item.userId}</TableCell>
                    <TableCell>{formatDate(item.timestamp)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FolderHistoryDialog;